/**
 * Plaid Connection Removal Page — DANGER ZONE
 *
 * Admin-only page for permanently removing a tenant's Plaid bank connection
 * both from Plaid and from the local database.
 *
 * Flow: tenant selector → connection selector → triple confirmation → removal.
 */

import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Divider,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  List,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Text,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import {
  MdAccountBalance,
  MdBlock,
  MdSearch,
  MdWarning,
} from 'react-icons/md';
import { adminApi } from '../../services/adminApi';
import { AdminTenant, PlaidConnectionInfo } from '../../types/admin';
import { toUserMessage } from '../../lib/apiError';

// ─── helpers ─────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function statusColor(status: string): string {
  if (status === 'ACTIVE') return 'green';
  if (status === 'LOGIN_REQUIRED') return 'orange';
  if (status === 'REVOKED') return 'red';
  return 'gray';
}

function connectionLabel(c: PlaidConnectionInfo): string {
  const name = c.institutionName ?? c.institutionId ?? 'Unknown institution';
  const synced = c.lastSyncedAt ? `Last synced ${formatDate(c.lastSyncedAt)}` : 'Never synced';
  return `${name}  •  Plaid  •  ${synced}`;
}

// ─── SearchableSelect ─────────────────────────────────────────────────────────

interface Option<T> {
  value: string;
  label: string;
  data: T;
}

interface SearchableSelectProps<T> {
  options: Option<T>[];
  value: string;
  onChange: (opt: Option<T>) => void;
  placeholder: string;
  isDisabled?: boolean;
  isLoading?: boolean;
}

function SearchableSelect<T>({
  options,
  value,
  onChange,
  placeholder,
  isDisabled = false,
  isLoading = false,
}: SearchableSelectProps<T>) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const hoverBg = useColorModeValue('red.50', 'red.900');
  const selectedLabel = options.find((o) => o.value === value)?.label ?? '';

  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function handleSelect(opt: Option<T>) {
    onChange(opt);
    setOpen(false);
    setQuery('');
  }

  const displayValue = open ? query : selectedLabel;

  return (
    <Box position="relative" ref={containerRef}>
      <InputGroup>
        <InputLeftElement pointerEvents="none">
          <Icon as={isLoading ? undefined : MdSearch} color="gray.400" />
        </InputLeftElement>
        <Input
          value={displayValue}
          placeholder={isLoading ? 'Loading...' : placeholder}
          isDisabled={isDisabled || isLoading}
          isReadOnly={!open}
          onClick={() => {
            if (!isDisabled && !isLoading) {
              setOpen(true);
              setQuery('');
            }
          }}
          onChange={(e) => setQuery(e.target.value)}
          cursor={isDisabled ? 'not-allowed' : 'pointer'}
          bg={bgColor}
          borderColor={open ? 'red.400' : borderColor}
          _focus={{ borderColor: 'red.400', boxShadow: '0 0 0 1px var(--chakra-colors-red-400)' }}
        />
      </InputGroup>

      {open && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          right={0}
          zIndex={50}
          bg={bgColor}
          border="1px"
          borderColor={borderColor}
          borderRadius="md"
          boxShadow="lg"
          maxH="260px"
          overflowY="auto"
        >
          {filtered.length === 0 ? (
            <Text px={4} py={3} fontSize="sm" color="gray.500">
              No results
            </Text>
          ) : (
            <List spacing={0}>
              {filtered.map((opt) => (
                <ListItem
                  key={opt.value}
                  px={4}
                  py={2}
                  fontSize="sm"
                  cursor="pointer"
                  bg={opt.value === value ? hoverBg : 'transparent'}
                  _hover={{ bg: hoverBg }}
                  onClick={() => handleSelect(opt)}
                >
                  {opt.label}
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      )}
    </Box>
  );
}

// ─── ConfirmModal ─────────────────────────────────────────────────────────────

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  tenantName: string;
  connectionLabel: string;
  institutionName: string;
  isRemoving: boolean;
}

const CONFIRM_PHRASE = 'REMOVE PLAID CONNECTION';

function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  tenantName,
  connectionLabel: connLabel,
  institutionName,
  isRemoving,
}: ConfirmModalProps) {
  const [typed, setTyped] = useState('');

  const dangerBg = useColorModeValue('red.50', 'red.950');
  const dangerBorder = useColorModeValue('red.200', 'red.800');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  function handleClose() {
    setTyped('');
    onClose();
  }

  function handleConfirm() {
    setTyped('');
    onConfirm();
  }

  const phraseMatches = typed === CONFIRM_PHRASE;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} isCentered size="lg">
      <ModalOverlay bg="blackAlpha.600" />
      <ModalContent>
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={MdWarning} color="red.500" boxSize={5} />
            <Text>Confirm Permanent Removal</Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack align="stretch" spacing={4}>
            {/* Summary */}
            <Box bg={dangerBg} border="1px" borderColor={dangerBorder} borderRadius="md" p={4}>
              <VStack align="stretch" spacing={2}>
                <HStack>
                  <Text fontSize="sm" fontWeight="semibold" minW="110px" color={mutedColor}>
                    Tenant:
                  </Text>
                  <Text fontSize="sm" fontWeight="bold">{tenantName}</Text>
                </HStack>
                <HStack>
                  <Text fontSize="sm" fontWeight="semibold" minW="110px" color={mutedColor}>
                    Connection:
                  </Text>
                  <Text fontSize="sm">{connLabel}</Text>
                </HStack>
                <HStack>
                  <Text fontSize="sm" fontWeight="semibold" minW="110px" color={mutedColor}>
                    Institution:
                  </Text>
                  <Text fontSize="sm" fontWeight="bold">{institutionName}</Text>
                </HStack>
              </VStack>
            </Box>

            <FormControl>
              <FormLabel fontSize="sm">
                Type <strong>{CONFIRM_PHRASE}</strong> to confirm
              </FormLabel>
              <Input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                placeholder={CONFIRM_PHRASE}
                fontFamily="mono"
                borderColor={phraseMatches ? 'red.500' : undefined}
                autoFocus
              />
            </FormControl>
          </VStack>
        </ModalBody>

        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            colorScheme="red"
            isDisabled={!phraseMatches}
            isLoading={isRemoving}
            loadingText="Removing…"
            onClick={handleConfirm}
          >
            Permanently Remove Connection
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export const PlaidRemovalPage: React.FC = () => {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const dangerBg = useColorModeValue('red.50', 'red.950');
  const dangerBorder = useColorModeValue('red.300', 'red.700');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');

  // Tenant list
  const [tenants, setTenants] = useState<AdminTenant[]>([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);

  // Selected tenant
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const selectedTenant = tenants.find((t) => t.id === selectedTenantId) ?? null;

  // Connections for selected tenant
  const [connections, setConnections] = useState<PlaidConnectionInfo[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);

  // Selected connection
  const [selectedConnectionId, setSelectedConnectionId] = useState('');
  const selectedConnection = connections.find((c) => c.id === selectedConnectionId) ?? null;

  // Removal in progress
  const [isRemoving, setIsRemoving] = useState(false);

  // Load all tenants on mount (paginate through if > 100)
  useEffect(() => {
    let cancelled = false;
    setTenantsLoading(true);

    async function loadAll() {
      const PAGE_SIZE = 100;
      const first = await adminApi.tenants.list({ limit: PAGE_SIZE, page: 1, sortBy: 'name', sortOrder: 'asc' });
      if (cancelled) return;

      let all = first.data;

      if (first.totalPages > 1) {
        const pageNums = Array.from({ length: first.totalPages - 1 }, (_, i) => i + 2);
        const rest = await Promise.all(
          pageNums.map((p) => adminApi.tenants.list({ limit: PAGE_SIZE, page: p, sortBy: 'name', sortOrder: 'asc' })),
        );
        if (cancelled) return;
        for (const r of rest) all = all.concat(r.data);
      }

      setTenants(all);
    }

    loadAll()
      .catch(() =>
        toast({
          title: 'Failed to load tenants',
          status: 'error',
          duration: 4000,
          isClosable: true,
        }),
      )
      .finally(() => { if (!cancelled) setTenantsLoading(false); });

    return () => { cancelled = true; };
  }, [toast]);

  // Load connections when tenant changes
  useEffect(() => {
    setSelectedConnectionId('');
    setConnections([]);

    if (!selectedTenantId) return;

    setConnectionsLoading(true);
    adminApi.tenants
      .getPlaidConnections(selectedTenantId)
      .then(setConnections)
      .catch(() =>
        toast({
          title: 'Failed to load Plaid connections',
          status: 'error',
          duration: 4000,
          isClosable: true,
        }),
      )
      .finally(() => setConnectionsLoading(false));
  }, [selectedTenantId, toast]);

  async function handleRemove() {
    if (!selectedTenantId || !selectedConnectionId) return;

    setIsRemoving(true);
    try {
      await adminApi.tenants.removePlaidConnection(selectedTenantId, selectedConnectionId);

      toast({
        title: 'Plaid connection removed successfully.',
        description: 'The tenant will need to reconnect their bank account through Plaid.',
        status: 'success',
        duration: 6000,
        isClosable: true,
      });

      // Reset state
      setSelectedConnectionId('');
      setConnections([]);
      setSelectedTenantId('');
      onClose();
    } catch (err) {
      const message = toUserMessage(err, 'Please try again in a moment.');
      toast({
        title: 'Unable to remove Plaid connection.',
        description: message,
        status: 'error',
        duration: 8000,
        isClosable: true,
      });
      onClose();
    } finally {
      setIsRemoving(false);
    }
  }

  const tenantOptions = tenants.map((t) => ({
    value: t.id,
    label: `${t.name}  (${t.slug})`,
    data: t,
  }));

  const connectionOptions = connections.map((c) => ({
    value: c.id,
    label: connectionLabel(c),
    data: c,
  }));

  const canRemove = Boolean(selectedTenantId && selectedConnectionId);

  return (
    <VStack spacing={6} align="stretch">
      {/* Page header */}
      <HStack spacing={3}>
        <Icon as={MdBlock} color="red.500" boxSize={7} />
        <Box>
          <Text fontSize="2xl" fontWeight="bold" color="red.600">
            DANGER ZONE
          </Text>
          <Text fontSize="sm" color={mutedColor}>
            Admin-only — Plaid connection removal
          </Text>
        </Box>
      </HStack>

      {/* Prominent warning */}
      <Alert
        status="error"
        borderRadius="lg"
        border="2px"
        borderColor={dangerBorder}
        bg={dangerBg}
        flexDirection="column"
        alignItems="flex-start"
        gap={2}
        p={5}
      >
        <HStack>
          <AlertIcon boxSize={5} />
          <AlertTitle fontSize="md">Read before proceeding</AlertTitle>
        </HStack>
        <AlertDescription fontSize="sm" lineHeight="tall">
          Use this only if the tenant is having problems connecting or reconnecting the same bank
          account in another application that also uses Plaid.
          <br />
          <br />
          <strong>This action permanently removes the Plaid connection.</strong>
          <br />
          The tenant will need to reconnect their bank account again through Plaid.
          <br />
          <strong>This action cannot be undone.</strong>
        </AlertDescription>
      </Alert>

      {/* Selection form */}
      <Box
        bg={bgColor}
        border="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={6}
      >
        <VStack align="stretch" spacing={6}>
          {/* Step 1 — Tenant */}
          <FormControl>
            <FormLabel fontWeight="semibold">
              Select Tenant
            </FormLabel>
            {tenantsLoading ? (
              <Skeleton h="40px" borderRadius="md" />
            ) : (
              <SearchableSelect
                options={tenantOptions}
                value={selectedTenantId}
                onChange={(opt) => setSelectedTenantId(opt.value)}
                placeholder="Search and select a tenant…"
              />
            )}
          </FormControl>

          {/* Step 2 — Connection */}
          <FormControl>
            <FormLabel fontWeight="semibold">
              Select Plaid Connection
            </FormLabel>
            {connectionsLoading ? (
              <Skeleton h="40px" borderRadius="md" />
            ) : (
              <SearchableSelect
                options={connectionOptions}
                value={selectedConnectionId}
                onChange={(opt) => setSelectedConnectionId(opt.value)}
                placeholder={
                  selectedTenantId
                    ? connections.length === 0
                      ? 'No Plaid connections found'
                      : 'Search and select a connection…'
                    : 'Select a tenant first'
                }
                isDisabled={!selectedTenantId || connections.length === 0}
              />
            )}

            {/* Connection detail badges */}
            {selectedConnection && (
              <HStack mt={3} spacing={2} flexWrap="wrap">
                <Badge colorScheme={statusColor(selectedConnection.status)} variant="subtle">
                  {selectedConnection.status}
                </Badge>
                <HStack spacing={1}>
                  <Icon as={MdAccountBalance} color={mutedColor} boxSize={3} />
                  <Text fontSize="xs" color={mutedColor}>
                    {selectedConnection.institutionName ?? 'Unknown institution'}
                  </Text>
                </HStack>
                <Text fontSize="xs" color={mutedColor}>
                  Connected {formatDate(selectedConnection.createdAt)}
                </Text>
                <Text fontSize="xs" color={mutedColor}>
                  Last synced {formatDate(selectedConnection.lastSyncedAt)}
                </Text>
              </HStack>
            )}
          </FormControl>

          <Divider />

          {/* Remove button — step 1 of 3 */}
          <Button
            colorScheme="red"
            size="lg"
            leftIcon={<Icon as={MdBlock} />}
            isDisabled={!canRemove}
            onClick={onOpen}
            alignSelf="flex-start"
          >
            Remove Plaid Connection
          </Button>

          {!canRemove && (
            <Text fontSize="xs" color={mutedColor}>
              Select a tenant and a Plaid connection to enable removal.
            </Text>
          )}
        </VStack>
      </Box>

      {/* Triple confirmation modal (steps 2 + 3 of 3) */}
      {selectedTenant && selectedConnection && (
        <ConfirmModal
          isOpen={isOpen}
          onClose={onClose}
          onConfirm={handleRemove}
          tenantName={selectedTenant.name}
          connectionLabel={connectionLabel(selectedConnection)}
          institutionName={
            selectedConnection.institutionName ?? selectedConnection.institutionId ?? 'Unknown'
          }
          isRemoving={isRemoving}
        />
      )}
    </VStack>
  );
};

export default PlaidRemovalPage;
